export interface InsuranceData {
  id: number;
  company_id: number;
  contact_id: number;
  policy_number: string;
  main_branch: string;
  sub_branch?: string;
  insurance_product_type?: string;
  handler_code: string;
  insurance_period?: string;
  insurance_start_date?: string; // ISO date string
  insurance_end_date?: string; // ISO date string
  additional_details?: string;
  premium_amount?: number;
  premium_type?: string;
  plan_classification?: string;
  reported_period: string; // ISO date string
  coverage_sum?: number;
  notes?: string;
  normalized_category?: HealthCoverageNormalizedCategory;
  created_at?: string;
  updated_at?: string;
}

export enum HealthCoverageNormalizedCategory {
  SURGERIES = "SURGERIES",
  TRANSPLANTS = "TRANSPLANTS",
  MEDICATIONS = "MEDICATIONS",
  AMBULATORY = "AMBULATORY",
  CONSULTATION_DIAGNOSIS = "CONSULTATION_DIAGNOSIS",
  DIGITAL_SERVICES = "DIGITAL_SERVICES",
  ALT_MEDICINE = "ALT_MEDICINE",
  CARE_MANAGEMENT = "CARE_MANAGEMENT",
  SPECIAL_SERVICES = "SPECIAL_SERVICES",
  CRITICAL_ILLNESS = "CRITICAL_ILLNESS",
  PERSONAL_ACCIDENTS = "PERSONAL_ACCIDENTS",
  DENTAL = "DENTAL",
  PACKAGE_UNKNOWN = "PACKAGE_UNKNOWN",
  NOISE = "NOISE",
  UNCLASSIFIED = "UNCLASSIFIED",
}

// MainInsuranceBranch.ts
export enum MainInsuranceBranch {
  CAR = "CAR",
  HOME = "HOME",
  BUSINESS = "BUSINESS",
  DISABILITY_LIFE = "DISABILITY_LIFE",
  LIFE = "LIFE",
  SAVINGS = "SAVINGS",
  HEALTH_ACCIDENT = "HEALTH_ACCIDENT",
  HEALTH = "HEALTH",
  LONG_TERM_CARE = "LONG_TERM_CARE",
  SERVICE_CONTRACT = "SERVICE_CONTRACT",
  LOSS_OF_WORK_CAPACITY = "LOSS_OF_WORK_CAPACITY",
}

export enum PlanClassification {
  INSURANCE = "INSURANCE",
  SERVICE = "SERVICE",
  PERSONAL = "PERSONAL",
  GROUP = "GROUP",
  KUPAT_HOLIM_GROUP = "KUPAT_HOLIM_GROUP",
}

export enum PremiumType {
  ANNUAL = "ANNUAL",
  MONTHLY = "MONTHLY",
  ONE_TIME = "ONE_TIME",
}

export enum InsuranceProductType {
  INSURANCE_POLICY = "INSURANCE_POLICY",
  ROADSIDE_SERVICE = "ROADSIDE_SERVICE",
  ROADSIDE_SERVICE_EXTENDED = "ROADSIDE_SERVICE_EXTENDED",
  REPLACEMENT_CAR = "REPLACEMENT_CAR",
  REPLACEMENT_CAR_HOME = "REPLACEMENT_CAR_HOME",
  WINDOWS = "WINDOWS",
  WINDOWS_EXTENDED = "WINDOWS_EXTENDED",
  LIGHTS_AND_MIRRORS = "LIGHTS_AND_MIRRORS",
  RADIO_REPLACEMENT = "RADIO_REPLACEMENT",
  WATER_DAMAGE = "WATER_DAMAGE",
  WATER_DAMAGE_EXTENDED = "WATER_DAMAGE_EXTENDED",
  LEGAL_ASSISTANCE = "LEGAL_ASSISTANCE",
  CONSULTATION = "CONSULTATION",
  SURGERY_STANDARD = "SURGERY_STANDARD",
  TRANSPLANT_STANDARD = "TRANSPLANT_STANDARD",
  MEDICINE_OUT_OF_BASKET = "MEDICINE_OUT_OF_BASKET",
  SURGERY_ABROAD_STANDARD = "SURGERY_ABROAD_STANDARD",
  ADVANCED_TREATMENT_EQUIPMENT = "ADVANCED_TREATMENT_EQUIPMENT",
  MEDICAL_SUPPORT = "MEDICAL_SUPPORT",
  AMBULATORY_OTHER = "AMBULATORY_OTHER",
  ALT_MEDICINE = "ALT_MEDICINE",
  RAPID_DIAGNOSIS = "RAPID_DIAGNOSIS",
  HOSPITAL_ESCORT = "HOSPITAL_ESCORT",
  ILLNESS_ESCORT = "ILLNESS_ESCORT",
  PERSONAL_MEDICAL_MANAGEMENT = "PERSONAL_MEDICAL_MANAGEMENT",
  ONLINE_DOCTOR = "ONLINE_DOCTOR",
  ONLINE_DOCTOR_ABROAD = "ONLINE_DOCTOR_ABROAD",
  SPORTS_MEDICINE = "SPORTS_MEDICINE",
  SUPPLEMENTARY_SURGERY_SHABAN = "SUPPLEMENTARY_SURGERY_SHABAN",
  SURGERY_INDEMNITY = "SURGERY_INDEMNITY",
  RANSPLANTS = "TRANSPLANTS",
  SURGERY_ABROAD = "SURGERY_ABROAD",
  BASIC_MEDICINES = "BASIC_MEDICINES",
}

export enum LongTermCareSubCategory {
  UP_TO_3_MONTHS = "UP_TO_3_MONTHS",
}

export enum CarInsuranceSubCategory {
  COMPULSORY = "COMPULSORY",
  COMPREHENSIVE = "COMPREHENSIVE",
  THIRD_PARTY = "THIRD_PARTY",
  COMPREHENSIVE_NO_ACCIDENT = "COMPREHENSIVE_NO_ACCIDENT",
  COMPREHENSIVE_NO_THEFT = "COMPREHENSIVE_NO_THEFT",
}

export enum HomeInsuranceSubCategory {
  BUILDING = "BUILDING",
  CONTENTS = "CONTENTS",
  COMBINED = "COMBINED",
  JEWELRY = "JEWELRY",
}

export enum BusinessInsuranceSubCategory {
  COMPREHENSIVE_BUSINESS = "COMPREHENSIVE_BUSINESS",
}

export enum DisabilityInsuranceSubCategory {
  DISABILITY_COMPENSATION = "DISABILITY_COMPENSATION",
  DISABILITY_WAIVER = "DISABILITY_WAIVER",
}

export enum ServiceContractSubCategory {
  ALT_MEDICINE = "ALT_MEDICINE",
  RAPID_DIAGNOSIS = "RAPID_DIAGNOSIS",
  HOSPITAL_ESCORT = "HOSPITAL_ESCORT",
  PERSONAL_MEDICAL_MANAGEMENT = "PERSONAL_MEDICAL_MANAGEMENT",
  ONLINE_DOCTOR = "ONLINE_DOCTOR",
  OTHER = "OTHER",
}

export enum LifeInsuranceSubCategory {
  PURE_SAVINGS = "PURE_SAVINGS",
  MIXED_SAVINGS = "MIXED_SAVINGS",
  DEATH_ONLY = "DEATH_ONLY",
  ACCIDENTAL_DEATH = "ACCIDENTAL_DEATH",
  DISABILITY = "DISABILITY",
}

export enum LossOfWorkCapacitySubCategory {
  DISABILITY_COMPENSATION = "DISABILITY_COMPENSATION",
  DISABILITY_WAIVER = "DISABILITY_WAIVER",
}

export enum HealthSubCategory {
  CONSULTATION = "CONSULTATION",
  ADVANCED_TREATMENT = "ADVANCED_TREATMENT",
  MEDICINES_OUTSIDE_BASKET = "MEDICINES_OUTSIDE_BASKET",
  SUPPLEMENTARY_SURGERY_SHABAN = "SUPPLEMENTARY_SURGERY_SHABAN",
  SURGERY_INDEMNITY = "SURGERY_INDEMNITY",
  SURGERY_STANDARD = "SURGERY_STANDARD",
  TRANSPLANTS = "TRANSPLANTS",
  RELOCATION = "RELOCATION",
  PERSONAL_ACCIDENTS = "PERSONAL_ACCIDENTS",
  SURGERY_ABROAD = "SURGERY_ABROAD",
  ALT_MEDICINE = "ALT_MEDICINE",
  RAPID_DIAGNOSIS = "RAPID_DIAGNOSIS",
  HOSPITAL_ESCORT = "HOSPITAL_ESCORT",
  ILLNESS_ESCORT = "ILLNESS_ESCORT",
  PERSONAL_MEDICAL_MANAGEMENT = "PERSONAL_MEDICAL_MANAGEMENT",
  ONLINE_DOCTOR = "ONLINE_DOCTOR",
  ONLINE_DOCTOR_ABROAD = "ONLINE_DOCTOR_ABROAD",
  SPORTS_MEDICINE = "SPORTS_MEDICINE",
  CRITICAL_ILLNESS = "CRITICAL_ILLNESS",
  BASIC_MEDICINES = "BASIC_MEDICINES",
  EXTENDED_MEDICINES = "EXTENDED_MEDICINES",
  COVERAGE_PACKAGE = "COVERAGE_PACKAGE",
  OTHER = "OTHER",
  BASIC_EXTENDED_MEDICINES = "BASIC_EXTENDED_MEDICINES",
  MEDICAL_EVENT_ESCORT = "MEDICAL_EVENT_ESCORT",
  REPLACEMENT_TREATMENTS = "REPLACEMENT_TREATMENTS",
  SURGERY_SHABAN_WITH_COPAY = "SURGERY_SHABAN_WITH_COPAY",
  SURGERY_WITH_COPAY = "SURGERY_WITH_COPAY",
  SURGERY_ACHIDA_SHABAN = "SURGERY_ACHIDA_SHABAN",
  SURGERY_ABROAD_ACHIDA = "SURGERY_ABROAD_ACHIDA",
  TRANSPLANT_ACHIDA = "TRANSPLANT_ACHIDA",
  MEDICINE_OUT_OF_BASKET_ACHIDA = "MEDICINE_OUT_OF_BASKET_ACHIDA",
  CONSULTATION_AND_TESTS = "CONSULTATION_AND_TESTS",
  ADVANCED_TREATMENT_AND_EQUIPMENT = "ADVANCED_TREATMENT_AND_EQUIPMENT",
  BASIC_MEDICINES_DASH = "BASIC_MEDICINES_DASH",
  EXTENDED_MEDICINES_DASH = "EXTENDED_MEDICINES_DASH",
  MEDICAL_EVENT_ESCORT_TEXT = "MEDICAL_EVENT_ESCORT_TEXT",
  DENTAL = "DENTAL",
  DENTAL_CARE = "DENTAL_CARE",
  DENTAL_TREATMENT = "DENTAL_TREATMENT",
  TEETH = "TEETH",
  TOOTH = "TOOTH",
}

